import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import React, { useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import Modal from "components/styled/modal/Modal";
import Section from "./Section";
import { getLocaleDateString, getLocaleTimeString } from "utils/dates";
import { PatientAppointment } from "features/appointments/PatientAppointments.types";
import { usePatientAppointments } from "features/appointments/hooks/usePatientAppointments.hook";

type PatientAppointmentSummaryVariant = "section" | "inline";

const toDate = (value?: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const byStartAsc = (a: PatientAppointment, b: PatientAppointment) => {
  const aDate = toDate(a.start)?.getTime() ?? 0;
  const bDate = toDate(b.start)?.getTime() ?? 0;
  return aDate - bDate;
};

const byStartDesc = (a: PatientAppointment, b: PatientAppointment) => byStartAsc(b, a);

export const getUpcomingAppointments = (appointments: PatientAppointment[], now = new Date()) => {
  return appointments
    .filter((appointment) => {
      const start = toDate(appointment.start);
      return start ? start > now : false;
    })
    .sort(byStartAsc);
};

export const getNextUpcomingAppointment = (appointments: PatientAppointment[], now = new Date()) => {
  return getUpcomingAppointments(appointments, now)[0];
};

const hasExplicitOffset = (value: string) => /[+-]\d{2}:?\d{2}$/.test(value);

const getLocalDateParts = (value: string) => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
  };
};

const getAppointmentDateString = (value: string) => {
  const parts = getLocalDateParts(value);
  if (!parts || !hasExplicitOffset(value)) return getLocaleDateString(value);

  return new Date(parts.year, parts.month - 1, parts.day).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const getAppointmentTimeString = (value: string) => {
  const parts = getLocalDateParts(value);
  if (!parts || !hasExplicitOffset(value)) return getLocaleTimeString(value);

  return new Date(2000, 0, 1, parts.hour, parts.minute).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "numeric",
  });
};

export const getPastAppointments = (appointments: PatientAppointment[], now = new Date()) => {
  return appointments
    .filter((appointment) => {
      const start = toDate(appointment.start);
      return start ? start < now : false;
    })
    .sort(byStartDesc);
};

const formatAppointmentDate = (appointment: PatientAppointment) =>
  appointment.start ? getAppointmentDateString(appointment.start) : "Unknown date";

const formatAppointmentTime = (appointment: PatientAppointment) => {
  if (!appointment.start) return "Unknown time";
  const start = getAppointmentTimeString(appointment.start);
  return appointment.end ? `${start} - ${getAppointmentTimeString(appointment.end)}` : start;
};

const isUrl = (value?: string) => Boolean(value?.match(/^https?:\/\//));

const EmptyState = ({ message, variant = "section" }: { message: string; variant?: PatientAppointmentSummaryVariant }) => (
  <Paper
    sx={{
      padding: (theme) => theme.spacing(variant === "inline" ? 2 : 3),
      borderRadius: (theme) => theme.spacing(2),
      color: (theme) => theme.palette.text.secondary,
    }}
  >
    {message}
  </Paper>
);

const AppointmentField = ({ label, value }: { label: string; value?: string }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: (theme) => theme.palette.text.secondary }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: "break-word" }}>
      {value || "-"}
    </Typography>
  </Box>
);

const AppointmentUrlField = ({ url }: { url: string }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="body2">
      <a href={url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
        Call URL
      </a>
    </Typography>
  </Box>
);

const AppointmentDetails = ({
  appointment,
  compact = false,
  showUrl = true,
}: {
  appointment: PatientAppointment;
  compact?: boolean;
  showUrl?: boolean;
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography variant="body1" sx={{ fontWeight: 700 }}>
      {appointment.appointment_type || "Appointment"}
    </Typography>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: compact ? "repeat(2, minmax(0, 1fr))" : "1fr",
        columnGap: (theme) => theme.spacing(2),
        rowGap: (theme) => theme.spacing(1),
      }}
    >
      <AppointmentField label="Type" value={appointment.appointment_type || "Appointment"} />
      <AppointmentField label="Date" value={formatAppointmentDate(appointment)} />
      <AppointmentField label="Time" value={formatAppointmentTime(appointment)} />
      <AppointmentField label="Calendar" value={appointment.calendar} />
      {showUrl && isUrl(appointment.location) ? (
        <AppointmentUrlField url={appointment.location as string} />
      ) : (
        !compact && <AppointmentField label="Location" value={appointment.location} />
      )}
    </Box>
  </Box>
);

const AppointmentGroup = ({
  title,
  appointments,
  emptyMessage,
}: {
  title: string;
  appointments: PatientAppointment[];
  emptyMessage: string;
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    {appointments.length ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {appointments.map((appointment, index) => (
          <Paper
            key={appointment.id || `${appointment.start}-${index}`}
            sx={{
              padding: (theme) => theme.spacing(2),
              borderRadius: 1,
              border: "1px solid",
              borderColor: (theme) => theme.palette.divider,
            }}
          >
            <AppointmentDetails appointment={appointment} />
          </Paper>
        ))}
      </Box>
    ) : (
      <Typography color="text.secondary">{emptyMessage}</Typography>
    )}
  </Box>
);

const PatientAppointmentSummaryContent = ({
  patientId,
  variant = "section",
}: {
  patientId?: string;
  variant?: PatientAppointmentSummaryVariant;
}) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const { data = [], isLoading, isError } = usePatientAppointments(patientId);

  const upcomingAppointments = useMemo(() => getUpcomingAppointments(data), [data]);
  const nextAppointment = upcomingAppointments[0];
  const pastAppointments = useMemo(() => getPastAppointments(data), [data]);
  const displayedAppointment = nextAppointment || pastAppointments[0];
  const displayedAppointmentLabel = nextAppointment ? "Next appointment" : "Most recent appointment";
  const appointmentCount = upcomingAppointments.length + pastAppointments.length;

  const Container = ({ children }: { children: React.ReactNode }) =>
    variant === "section" ? <Section title="Appointments">{children}</Section> : <>{children}</>;

  if (isLoading) {
    return (
      <Container>
        <Paper
          sx={{
            padding: (theme) => theme.spacing(variant === "inline" ? 2 : 3),
            borderRadius: (theme) => theme.spacing(2),
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} />
        </Paper>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>
        <EmptyState message="Unable to load appointments" variant={variant} />
      </Container>
    );
  }

  return (
    <Container>
      {displayedAppointment ? (
        <>
          <Paper
            sx={{
              width: "100%",
              borderRadius: (theme) => theme.spacing(2),
              border: "1px solid",
              borderColor: (theme) => theme.palette.divider,
              backgroundColor: (theme) => theme.palette.common.white,
              textAlign: "left",
              cursor: "pointer",
              "&:hover": {
                borderColor: (theme) => theme.palette.primary.main,
              },
            }}
          >
            <Box
              component="button"
              type="button"
              aria-label="View appointment history"
              onClick={() => setHistoryOpen(true)}
              sx={{
                all: "unset",
                boxSizing: "border-box",
                width: "100%",
                padding: (theme) => theme.spacing(variant === "inline" ? 2 : 3),
                cursor: "pointer",
              }}
            >
              <Typography variant="subtitle2" sx={{ color: (theme) => theme.palette.primary.main }}>
                {displayedAppointmentLabel}
              </Typography>
              <Typography variant="caption" sx={{ color: (theme) => theme.palette.text.secondary }}>
                {appointmentCount} appointment{appointmentCount === 1 ? "" : "s"}
              </Typography>
              <AppointmentDetails
                appointment={displayedAppointment}
                compact={variant === "inline"}
                showUrl={false}
              />
            </Box>
            {isUrl(displayedAppointment.location) && (
              <Box
                sx={{
                  px: (theme) => theme.spacing(variant === "inline" ? 2 : 3),
                  pb: (theme) => theme.spacing(2),
                }}
              >
                <AppointmentUrlField url={displayedAppointment.location as string} />
              </Box>
            )}
          </Paper>
          <Modal open={historyOpen} onClose={() => setHistoryOpen(false)} title="Appointment history">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <AppointmentGroup
                title="Upcoming appointments"
                appointments={upcomingAppointments}
                emptyMessage="No upcoming appointments"
              />
              <AppointmentGroup
                title="Past appointments"
                appointments={pastAppointments}
                emptyMessage="No past appointments"
              />
            </Box>
          </Modal>
        </>
      ) : (
        <EmptyState message="No appointments" variant={variant} />
      )}
    </Container>
  );
};

const PatientAppointmentSummary = ({
  patientId,
  variant = "section",
}: {
  patientId?: string;
  variant?: PatientAppointmentSummaryVariant;
}) => (
  <ErrorBoundary fallback={<EmptyState message="Unable to load appointments" variant={variant} />}>
    <PatientAppointmentSummaryContent patientId={patientId} variant={variant} />
  </ErrorBoundary>
);

export default PatientAppointmentSummary;
